<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>

        <div
            v-if="initialLoading"
            key="initialLoading"
            class="animate__animated animate__fadeIn animate_fadeOut animate__fast bg-gray-50 rounded-lg"
        >
            <!-- <busy-spinner inner-class="my-5" text="Loading..."></busy-spinner> -->
            <b-skeleton-table :rows="perPage" :columns="fieldsToShow.length" show-footer :table-props="{bordered: true}"></b-skeleton-table>
        </div>

        <div 
            v-if="!initialLoading"
            key="initialLoadingDone"
        >
            <div 
                v-if="hasAnyFilters"
                class="d-flex flex-wrap justify-content-between align-items-end mb-3 animate__animated animate__fast animate__fadeInUp"
            >  <!-- Filter Area -->
                <div>  <!-- no padding on this... so it collapses to nothing if no search box -->
                    <base-search-box
                        v-if="isFilterEnabled('search')"
                        :value="currentFilterValues.search"
                        ref="searchBox"
                        placeholder="Quick Search..."
                        @search="setFilterAndSearch('search', $event)"
                        @reset="setFilterAndSearch('search', '')"
                    ></base-search-box>
                </div>
                <b-form inline class="mt-2">
                    <div class="align-self-end">
                        <b-button 
                            variant="soft-primary" 
                            :disabled="!showFilterReset" 
                            class="text-uppercase" 
                            @click="clearAllFilters"
                        >
                            Reset
                        </b-button>
                    </div>
                    <base-filter-select
                        label="My Role"
                        v-if="isFilterEnabled('grouping')"
                        v-model="currentFilterValues.grouping"
                        :reset-value="initialFilterValues.grouping"
                        :options="groupingOptions"
                        @change="getNewList"
                    ></base-filter-select>
                    <base-filter-select
                        label="Type"
                        v-if="isFilterEnabled('workflow_spec')"
                        v-model="currentFilterValues.workflow_spec"
                        :reset-value="initialFilterValues.workflow_spec"
                        first-option-text="All Types"
                        :options="workflowSpecOptions"
                        text-field="label"
                        @change="getNewList"
                    ></base-filter-select>
                    <!-- you need options for any vendor, no vendor -- add explicit FilterSet for this -->
                    <base-filter-select
                        label="Vendor"
                        v-if="isFilterEnabled('vendor')"
                        v-model="currentFilterValues.vendor"
                        :reset-value="initialFilterValues.vendor"
                        first-option-text="All"
                        :options="vendorOptions"
                        text-field="label"
                        @change="getNewList"
                    ></base-filter-select>
                    <base-filter-select
                        label="Status"
                        v-if="isFilterEnabled('status')"
                        v-model="currentFilterValues.status"
                        :reset-value="initialFilterValues.status"
                        :options="statusOptions"
                        value-field="value"
                        text-field="text"
                        @change="getNewList"
                    ></base-filter-select>  <!-- first-option-text="Any Status" only used when setting blank first option -->
                    <b-button
                        v-b-tooltip.hover title="Refresh List"
                        variant="light"
                        class="ml-2 align-self-end"
                        @click="refreshList(true)"
                    >
                        <b-icon icon="arrow-repeat"></b-icon>
                    </b-button>
                </b-form>
            </div>

            <b-card 
                id="rfx-table-card"
                class="rounded-lg animate__animated animate__fadeIn animate__fast"
                :class="noShadow ? '' : 'shadow'"
                no-body
            >

                <transition name="fade-in-fast" mode="out-in">

                    <div
                        v-if="!totalRows"
                        :key="`emptyList${paramsHashString}`"
                        class="d-flex justify-content-center align-items-center"
                        style="min-height: 200px;"
                    >
                        <transition name="fade-in-fast" mode="out-in">
                            <div v-if="isUpdatePending || loading" :key="`updating${paramsHashString}`" class="">
                                <b-spinner></b-spinner>
                            </div>
                            <div v-else-if="!loading && !isUpdatePending" key="updateNotPending" class="text-center">
                                <div class="font-size-100 text-muted mb-4">
                                    <div class="">
                                        {{ showFilterReset ? `No ${toLowerCase(localItemNamePlural)} found.  Try a different search` : `No ${toTitleCase(localItemNamePlural)}` }}
                                    </div>
                                    <div class="text-muted small mt-1">
                                        {{ (isFilterEnabled('grouping') && currentFilterValues.grouping === 'participant') ? '(filtered as Owner or Team Member)' : '' }}
                                    </div>                                    
                                </div>
                                <b-button v-if="showFilterReset" variant="soft-primary" @click="clearAllFilters">
                                    Clear Search
                                </b-button>
                                <b-button v-if="showCreateButton" size="lg" variant="soft-success" @click="handleAddNew">
                                    Create New {{ toTitleCase(localItemName) }}
                                </b-button>
                            </div>
                        </transition>
                    </div>

                    <div
                        v-else
                        key="notEmptyList"
                    >

                        <b-table 
                            :items="itemList"
                            :fields="fieldsToShow"
                            :busy="loading"
                            primary-key="id" 
                            responsive
                            no-local-sorting
                            no-sort-reset
                            :sort-by.sync="sortByField"
                            :sort-desc.sync="sortDesc"
                            @sort-changed="getNewList"
                            hover
                            tbody-class="cells-align-middle"
                            class="no-top-border td-align-middle mb-0"
                            thead-class="overflow-hidden cells-align-middle"
                            head-variant=""
                        >
                            <!-- <template v-slot:table-busy>
                                <div class="text-center text-primary my-2">
                                    <b-spinner class="align-middle"></b-spinner>
                                    <strong>Loading...</strong>
                                </div>
                            </template> -->
                            <template v-slot:cell(title)="data">
                                <b-link :href="getListUrl(data.item)">{{ data.value }}</b-link>
                                <b-badge 
                                    v-if="data.item.is_waiting_on_tu" 
                                    variant="soft-danger" 
                                    v-b-tooltip.hover
                                    title="Waiting on your action"
                                    class="ml-2 text-uppercase"
                                >You</b-badge>
                            </template>
                            <template #cell(workflow_run_number)="data">
                                <div>{{ data.value }}</div>
                                <div 
                                    v-if="!fieldsToShow.includes('workflow_spec')"
                                    class="text-dark font-weight-500 small mt-1"
                                >
                                    {{ data.item.workflow_spec.name }}
                                </div>
                            </template>
                            <template #cell(status)="data">
                                <status-object-badge
                                    :status-object="data.item.status_object" 
                                    text-uppercase
                                    font-size="0.8rem"
                                    title-field="title"
                                    value-field="value"
                                    no-wrap
                                ></status-object-badge>
                            </template>
                            <template v-slot:cell(active_tasks)="data">
                                <span v-if="!data.item.has_active_tasks" class="text-muted">-</span>
                                <div v-else class="">
                                    <!-- just show first task and (+ 2 more tasks...)? -->
                                    <div v-for="task in data.value" :key="task.id">
                                        <div>
                                            {{ truncateTaskTitle(task.title) }}
                                            <b-badge v-if="task.is_overdue" variant="soft-danger" class="text-uppercase ml-1">Overdue</b-badge>
                                        </div>
                                        <div class="text-muted small">{{ stringifyAssignedTus(task.assigned_tus) }}</div>
                                    </div>
                                </div>
                            </template>
                            <template #cell(created)="data">
                                {{ data.value | dateFilter }}
                            </template>
                            <template #cell(modified)="data">
                                {{ data.value | dateFilter }}
                            </template>
                        </b-table>
                    </div>
                
                </transition>

                <template #footer v-if="!loading && totalRows">
                    <base-table-pagination
                        :current-page.sync="currentPage"
                        :per-page.sync="perPage"
                        :total-items="totalRows"
                        :show-per-page="showPerPage"
                        :item-name="localItemNamePlural"
                        @update:perPage="getNewList"
                        @update:currentPage="getList"
                    ></base-table-pagination>
                </template>
            </b-card>

        </div>

    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import BaseTablePagination from '../BaseTablePagination';
import { FormatDateHelper, FormatDateTimeLocal, HumanizeDateTimeHelper } from '../../js/filters-helpers';
//import DateTimeDeadline from '../table-cell-comps/DateTimeDeadline';
import BaseSearchBox from '../BaseSearchBox';
import BaseFilterSelect from '../BaseFilterSelect';
import StatusObjectBadge from '../functional-comps/StatusObjectBadge';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import truncate from 'lodash/truncate';
import capitalize from 'lodash/capitalize';
//import cloneDeep from 'lodash/cloneDeep';
import invoke from 'lodash/invoke';
import get from 'lodash/get';
import { errorHandling, splTableDefaults, currentUserInfoMixin } from '../../js/custom-vue-mixins';
import { WorkflowRun, getWorkflowStatusOptions } from './workflow_models';


export default {
    name: 'WorkflowRunList',
    components: {
        BaseTablePagination,
        BaseSearchBox,
        BaseFilterSelect,
        StatusObjectBadge,
        //DateTimeDeadline,
    },
    mixins: [currentUserInfoMixin, errorHandling],
    props: {
        showFields: {  // aray of strings of fields to show, based on 'key' values in internal field defs
            type: Array,
            required: true,
        },
        showFilters: {  // array of strings that correspond to which filters to show
            // pass empty list to hide all filters -- leave off (no prop) to show all filters
            type: Array,
            default: () => {
                return ['search', 'grouping', 'status'];  // 'sm_state'
            }
        },
        presetFilters: { // pass in an object in the same format as defaultFilterValues with any filters you want to pre-set
            // if you also hide that same filter, then it will be 'hard-coded' and not changeable by user
            // this lets you use an existing endpoint, but with hard-coded filter values, like {grouping: 'owned'}
            // how it works:  we use Object.assign() to merge this with defaultFilterValues on created --> set that as a new object on initialFilterValues
            type: Object,
            required: false,
        },
        endpointPath: {  // dotted path of endpoint on suppliosApi - ie: 'workflowRun.get_list_internal' 
            type: String,
            default: 'workflowRun.get_list_internal',
        },
        urlPath: {  // dotted path to URL from the top-level of item - so if all urls are in nested .urls then use 'urls.internal_detail'
            // but may not want to use this... because even for internal users, you want to link to setup or detail depending on status
            type: String,
            default: 'urls.internal_detail',
        },
        initialSortField: {  // initial field for sortBy, does not have to be in showFields array, but must be in fields array and must be in the ordering options for API endpoint
            type: String,
            default: 'created',
        },
        initialSortDesc: {  // initial sort in descending order?
            type: Boolean,
            default: false,
        },
        isInternal: {  // set true for using in internal tenant use cases -- turns things on/off like add new button in empty list, etc.
            type: Boolean,
            default: false,
        },
        specGroupSlugs: {  // string of DemoWorkflowSpecGroup.slug or array of them -- will limit the WorkflowRuns to these groups
            type: [String, Array],
            default: '',
        },
        vendorId: {
            // for hard-coding a filter for vendor ID
            type: String,
            default: '',
        },
        fixedPerPage: {  // if you want to force a fixed # per page AND HIDE PER-PAGE SELECTOR
            // this is useful for dashboard views where you want to control size of grid
            // validator... we use same one in base-table-pagination component, so using here too
            type: [String, Number],
            required: false,
            validator: function (value) {
                return splTableDefaults.perPageOptions.includes(value);
                //return defaultPerPageOptions.includes(value);
            },
        },
        noShadow: {  // remove shadow from the table card
            type: Boolean,
            default: false,
        },
        itemName: {
            type: String,
            default: "Workflow"
        },
        itemNamePlural: {
            type: String,
            required: false,  // we just add 's' on itemName by default
        },
        hideCreateButton: {
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            initialLoading: true,  // set to false after getting list the first time
            loading: false,  // loading signal for fetching list
            isUpdatePending: false,  // set when you are about to refresh things... to prevent flashes of 'no items found'
            lastLoadWasEmpty: true,  // used as part of v-if for showing placeholders
            currentPage: 1,  // will get sync'd by base-table-pagination
            perPage: 25,  // will get sync'd by base-table-pagination, and also set by our mixin default value during create hook - NEW - could be overridden if fixedPerPage is set!
            showPerPage: true,  // if we set fixedPerPage prop, this is set false in created() - and passed to same prop in pagination component
            totalRows: null,  // total items in list... set from response.data.count
            itemList: [],   // list from API
            fields: [
                // for column widths, do not set fixed or responsive, then use class: on field def object with either min-width or max-width (but not width directly)
                // make this a PROP to change dynamically... even if you only listen to it on initial created/mounted... then you can use for many different WfSpecs
                {
                    key: 'title',
                    label: 'Issue Title',
                    sortable: true,
                    api_ordering_name: 'title',  // set this for any sortable field, this is what gets sent to API for ordering=_____
                    tdClass: 'font-weight-bold'
                },{
                    key: 'workflow_run_number',
                    label: 'Number',
                    sortable: true,
                    api_ordering_name: 'workflow_run_number',  // set this for any sortable field, this is what gets sent to API for ordering=_____
                    tdClass: 'min-width-140'
                },{
                    key: 'workflow_spec',
                    label: 'Issue Type',
                    sortable: true,
                    api_ordering_name: 'workflow_spec__name',  // set this for any sortable field, this is what gets sent to API for ordering=_____
                    tdClass: '',
                    formatter: (value) => value.name,
                },{
                    key: 'vendor',
                    label: 'Vendor',
                    sortable: true,
                    api_ordering_name: 'vendor__name',
                    formatter: (value) => value.name,
                },{
                    key: 'status',  // just a front-end thing, from our WorkflowRun class
                    label: 'Status',
                    sortable: false,
                    //api_ordering_name: 'sm_state',
                    class: 'text-center max-width-120'
                },{
                    key: 'active_tasks',
                    label: 'Waiting On',
                    sortable: false,
                },{
                    key: 'past_due_tasks',  // just use active_tasks instead and highlight if deadline is past
                    label: 'Past Due Items',
                    sortable: false,
                },{
                    key: 'created',
                    label: 'Date Created',
                    sortable: true,
                    api_ordering_name: 'created',
                    class: 'text-center'
                },{
                    key: 'modified',
                    label: 'Last Modified',
                    sortable: true,
                    api_ordering_name: 'modified',
                    class: 'text-center'
                },/* {
                    key: 'actions',
                    label: 'Actions',
                    class: 'text-center',
                } */
            ],
            // FILTERS & GROUPING
            paramsHashString: '',  // we stringify the current filters, just for using as a :key in template to refresh things
            loadingSetupData: false,  // flag for loading setup data
            currentFilterValues: {},  // set this using Object.assign({}, this.initialFilterValues) during created hook
            initialFilterValues: {},  // we merge this.defaultFilterValues with any presetFilters (prop) in created hook to make this
            defaultFilterValues: {  // Object.assign({}, initialFilterValues) on create, and when you reset
                // need one here for every possible filter option, unless you add more dynamically from backend
                search: '',
                grouping: 'participant',  // groupings by current TenantUser visibility
                status: 'not_completed',
                workflow_spec: '',
                vendor: '',  // will be vendor ID, if we have the filter enabled
            },
            groupingOptions: [  // make this computed based on currentUserInfo role?  
                {
                    value: 'all',
                    text: 'All',
                },{
                    value: 'owned',
                    text: 'Owner'
                },{
                    value: 'participant',
                    text: 'Owner or Team Member'
                }
            ],
            statusOptions: [],  // set in created from the function we imported to get the same list we use in WorkflowRun class instances
            hasAppliedFilters: false,  // set this each time we getList() based on whether we used filters or not
            // for sorting:
            sortByField: 'close_date',  // default gets set via prop... sortByField and sortDesc will get .sync'd by b-table, then gets used in API call query params
            sortDesc: true,  // sort descending = true (adds '-' sign in backend), sort ascending = false (no minus sign)
            // FILTERS FROM BACKEND - using filterset in list action
            extraFilters: [],  // array of objects of extra filters?  
            // TODO - you should merge all filters so they act the same
            // as of Aug 1 2022 - you are returing some filters with the list, but not all, and you are not handling them all consistently in this component - refactor needed!
            // TEMPORARY - Aug 1 2022 - stash things from workflow_spec filter, pulled out of extraFilters
            workflowSpecOptions: [],  // and stash default value in defaultFilterValues and initialFilterValues 
            vendorOptions: [],  // doing same for vendor filter also... might not get anything from backend for EXTERNAL TUs
        }
    },
    computed: {
        showFilterReset: function() {
            // returns BOOLEAN if any currentFilterValues are different than initialFilterValues
            let isSame = isEqual(this.currentFilterValues, this.initialFilterValues);
            return !isSame;
        },
        fieldsToShow: function() {
            // determine our final list of fields to show based on this.showFields prop and this.fields complete list
            return this.fields.filter(fld => this.showFields.includes(fld.key));
        },
        hasAnyFilters: function() {
            // determine if there are ANY filters specified to be shown based on showFilters prop
            // if so, return true, if no filters shown, return false
            // used to show/hide the filter area completely
            return this.showFilters.length > 0;
        },
        skeletonRows() {
            // return the number of rows to show in skeleton - based on a default, and any fixedPerPage, etc.
            return this.perPage;  // header and footer are added separately
        },
        localSpecGroupSlugs() {
            // return ARRAY of slugs for the DemoWorkflowSpecGroups to include in this list
            // take from prop specGroupSlugs -- and make sure its an array
            // your internal ones are: '_quality_module' and '_onboarding_module'... and more...
            return Array.isArray(this.specGroupSlugs) ? [...this.specGroupSlugs] : (this.specGroupSlugs ? [this.specGroupSlugs] : []);
        },
        localItemName() {
            return this.itemName;
        },
        localItemNamePlural() {
            return this.itemNamePlural ?? `${this.itemName}s`;
        },
        showCreateButton() {
            return !this.hideCreateButton && !this.showFilterReset && this.isInternal && this.$currentUserInfo.is_paid_seat
        },
    },
    filters: {
        dateFilter: function(value) {
            return FormatDateHelper(value);
        },
        dateTimeFilter: function(value) {
            return FormatDateTimeLocal(value);
        },
        humanizeDateTime: function(value) {
            return HumanizeDateTimeHelper(value);
        },
    },
    created: function() {
        // set options for statusOptions 
        const specificOptions = getWorkflowStatusOptions();  // should be 'initial', 'active', 'final'
        const allStatusOption = {value: 'all', text: 'All'};  // put it last
        const overdueOption = {value: 'overdue', text: 'Overdue'};
        const notCompletedOption = {value: 'not_completed', text: 'Not Closed'};  // draft + active
        this.statusOptions = [ notCompletedOption, ...specificOptions, overdueOption, allStatusOption];
        // merge any presetFilters (object from prop) with this.defaultFilterValues to make this.initialFilterValues
        const vendorFilterHardCode = this.vendorId ? { vendor: this.vendorId } : {};
        const defaultsAndHardcode = Object.assign({}, this.defaultFilterValues, vendorFilterHardCode);
        if (this.presetFilters && !isEmpty(this.presetFilters)) {
            // target is empty object so you don't mutate originals, merge presetFilters last
            this.initialFilterValues = Object.assign({}, defaultsAndHardcode, this.presetFilters);  
        } else {
            // if we DO NOT have any presetFilters, we need to just do same thing, but without presetFilters... maybe there's a shorter way, but this works
            this.initialFilterValues = Object.assign({}, defaultsAndHardcode);
        }
        this.resetFilterValues();
        // FIGURE OUT THE perPage to use
        if (this.fixedPerPage) {
            this.perPage = Number(this.fixedPerPage);
            this.showPerPage = false;
        } else {
            this.perPage = splTableDefaults.perPage;  // splTableDefaults is from import
            this.showPerPage = true;
        }
        this.sortByField = this.initialSortField;  // set initial sort field based on prop
        this.sortDesc = this.initialSortDesc;  // set initial sort direction based on prop
    },
    mounted: function () {
        //this.getSetupData();
        this.getList();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        // PUBLIC METHOD YOU CAN CALL FROM PARENT TO REFRESH LIST, likely just as $refs.something.refreshList();
        refreshList: function(noFilterReset = false) {
            if (noFilterReset) {
                this.getNewList();  // if you specifically set noFilterReset to true (first positional arg), then don't reset filters (but do reset current page to 1)
            } else {
                this.clearAllFilters();   // this resets all filter values and reloads the list
            }
        },
        // internal methods
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        resetFilterValues: function() {
            this.currentFilterValues = Object.assign({}, this.initialFilterValues);
            // new way
            const filterRefs = ['searchBox'];
            filterRefs.forEach(refName => {
                if (this.$refs[refName]) {
                    this.$refs[refName].reset();
                }
            });
        },
        setFilterAndSearch(filterKey, value) {
            // pass in which filter to set and the value
            console.log('filterKey and value', filterKey, value);
            this.currentFilterValues[filterKey] = value;
            this.getNewList();
        },
        clearAllFilters: function() {
            // clear/reset all filters and search
            this.resetFilterValues();
            this.getNewList();
        },
        getOrderingFieldString() {
            // look up what field name (string) we should use to send to API, in case key here is different than what API should order by
            // useful if you have serializer methods that get values from related objects... you can use double-underscore notation in DRF for ordering
            // also add '-' if sortDesc is true
            let apiOrderingName = this.fields.find(fld => fld.key === this.sortByField).api_ordering_name;
            return (this.sortDesc ? '-' : '') + apiOrderingName;
        },
        getNewList() {
            // call getList(), but must reset currentPage first
            // call this from any change to filters, sort, search, etc
            this.isUpdatePending = true;  // to remove the 'no items found' temporarily
            this.paramsHashString = '_reset_';  // to immediately reset this, so keyed template things update right away
            console.log('update pending', this.paramsHashString);
            this.currentPage = 1;
            this.$nextTick(() => this.getList());
        },
        async getList() {  
            this.loading = true;
            this.$emit('list-loading');  // tell parent we are loading
            // axiosConfig['params'] is where we put search/filter params
            let filterParams = {  // grab the current search/filter values -- need to do this dynamically when we have that
                search: this.currentFilterValues.search || null,
                grouping: this.currentFilterValues.grouping, 
                spec_groups: this.localSpecGroupSlugs,
                status: this.currentFilterValues.status || null,
                vendor: this.currentFilterValues.vendor || null,  
            };
            if ('workflow_spec' in this.currentFilterValues) {
                filterParams['workflow_spec'] = this.currentFilterValues.workflow_spec;
            }
            let orderingParams = {
                ordering: this.getOrderingFieldString(),
            };
            let paginationParams = {
                page: this.currentPage,
                page_size: this.perPage,
            };
            let combinedParams = Object.assign({}, filterParams, orderingParams, paginationParams);
            this.paramsHashString = JSON.stringify(combinedParams);  // just for using as a :key in template - we also update in getNewList to make sure it 'resets' immediately
            console.log('paramsHashString', this.paramsHashString);
            // if you use URLSearchParams, it does not append '[]' on params that are a list (like axios does on its own)
            // but it also includes params that have falsey/empty values - will do this with empty search query:  'search': ['null'] 
            // we'll use the axios default, then on backend, we'll check for both 'spec_groups[]' and 'spec_groups'
            let axiosConfig = {params: combinedParams};
            //suppliosApi.rfx[this.endpointName](axiosConfig)
            try {
                const response = await invoke(suppliosApi, this.endpointPath, axiosConfig);
                this.totalRows = response.data.count || 0;
                //this.itemList = response.data.results;
                this.itemList = response.data.results.map(item => new WorkflowRun(item));
                const extraFilters = response.data.filters?.filters;
                this.extraFilters = extraFilters ?? {};
                // As of Aug 1 2022 - still doing filters the old way... so pull out filter data for workflow_spec and make it work with other old-style filters
                if (extraFilters && 'workflow_spec' in extraFilters) {
                    // only do this on first load??? not if options change based on filtered list...
                    // you may not get everything from backend if no options, no default value, etc. - so check each one
                    const wfSpecFilterData = extraFilters.workflow_spec;
                    const opts = wfSpecFilterData.options;
                    this.workflowSpecOptions = (opts && Array.isArray(opts)) ? [...opts] : [];
                    const queryParam = wfSpecFilterData.query_param;  // should always have this one
                    if (queryParam in this.currentFilterValues) {
                        // do not add it... already there
                    } else {
                        this.$set(this.currentFilterValues, queryParam, defaultValue);
                    }
                    const defaultValue = wfSpecFilterData.default_value;  // may not have this, if there were no options in backend 
                    if (defaultValue !== undefined) {  // default value will likely be falsey ('', null, etc.) so must check if undefined
                        this.$set(this.defaultFilterValues, queryParam, defaultValue);
                        this.$set(this.initialFilterValues, queryParam, defaultValue);
                    }
                    // don't mess with current value?  or add it if its not already in current values?
                }
                // AND SAME THING FOR VENDOR FILTER NOW - we may not have this one from backend, if for external TU
                if (extraFilters && 'vendor' in extraFilters) {
                    // only do this on first load??? not if options change based on filtered list...
                    // you may not get everything from backend if no options, no default value, etc. - so check each one
                    const vendorFilterData = extraFilters.vendor;
                    const opts = vendorFilterData.options;
                    this.vendorOptions = (opts && Array.isArray(opts)) ? [...opts] : [];
                    const queryParam = vendorFilterData.query_param;  // should always have this one
                    if (queryParam in this.currentFilterValues) {
                        // do not add it... already there
                    } else {
                        this.$set(this.currentFilterValues, queryParam, defaultValue);
                    }
                    const defaultValue = vendorFilterData.default_value;  // may not have this, if there were no options in backend
                    if (defaultValue !== undefined) {  // default value will likely be falsey ('', null, etc.) so must check if undefined
                        this.$set(this.defaultFilterValues, queryParam, defaultValue);
                        this.$set(this.initialFilterValues, queryParam, defaultValue);
                    }
                    // don't mess with current value?  or add it if its not already in current values?
                }
                this.$emit('list-loaded', this.totalRows);  // emit total count to parent
                // setting hasAppliedFilters must be done once we get the response, not before
                this.hasAppliedFilters = Object.values(filterParams).some(val => !!val);  // goes through all filter values, if any are not empty/null, returns true
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.lastLoadWasEmpty = this.itemList.length < 1;
            this.initialLoading = false;  // set flag to say we already loaded once
            this.loading = false;
            this.isUpdatePending = false;
        },
        handleAddNew: function() {
            // just emits something to parent so parent can call method on add new event component
            this.$emit('request-create');
        },
        isFilterEnabled: function(filterName) {
            // checks if a specific filter is enabled
            // Aug 1 2022 - will also check if we have key for it in currentFilterValue -- otherwise its not ready yet (if adding dynamic filters from backend)
            return this.showFilters.includes(filterName) && filterName in this.currentFilterValues;
        },
        getFromItem(item, dottedPath) {
            // helper to get something from a single item in the list using lodash 'get'
            return get(item, dottedPath);
        },
        getListUrl(item) {
            // pass in the full item, this will return the URL path to use, based on if its in draft or if launched
            if (!this.$currentUserInfo.is_internal) {
                return item.urls.external_detail;
            } else if (item.current_step_is_initial) { /// if not yet started, return setup URL
                return item.urls.internal_setup;
            } else {
                return item.urls.internal_detail;
            }
        },
        truncateTaskTitle(taskTitle) {
            // for making shorter task names for 'Active Task' ('Waiting On') if they get long
            // happens for auto-generated tasks, like corrective action verification tasks
            return truncate(taskTitle, {
                length: 60,
                omission: '...',
                separator: ' ',
            });
        },
        stringifyAssignedTus(assignedTusArray) {
            // pass in array of assigned_tus (likely in template) and return a stringified version 
            // truncate the list if its long
            const displayLimit = 3;
            if (assignedTusArray && Array.isArray(assignedTusArray) && assignedTusArray.length > 0) {
                const extra = Math.max((assignedTusArray.length - displayLimit), 0);
                const toDisplay = assignedTusArray.slice(0, displayLimit).map(tu => tu.full_name).join(', ');
                return extra > 0 ? `${toDisplay} + ${extra} others` : toDisplay;
            } else {
                return 'No Assignees';
            }
        },
        toTitleCase(value) {
            // title case each word:  'quality issue' -> 'Quality Issue'
            return String(value).split(' ').map(w => capitalize(w)).join(' ');
        },
        toLowerCase(value) {
            return String(value).toLowerCase();
        },
    }
}
</script>

<style>

</style>
